/**
 * The client half of the funnel.
 *
 * Six events, one bus, and zero sinks until somebody configures one. The
 * taxonomy, the vocabularies and the reasoning all live in
 * `zentos_web/measure.py`; this file is the emitter, and it is deliberately
 * the smallest thing that can be correct.
 *
 * WHAT IT DOES WHEN NOTHING IS CONFIGURED
 *
 * Nothing. Not "sends to a queue that is never flushed", not "writes to
 * localStorage in case somebody wants it later" -- nothing. `track()` builds
 * the event, finds no sinks attached, and returns. No request, no storage, no
 * cookie, no script tag, no global touched. That is the same rule
 * `turnstile.js` follows when no site key is rendered and `links.py` follows
 * when no WhatsApp number is set: absent configuration does nothing at all,
 * rather than doing something harmless-looking to somebody.
 *
 * The site ships in exactly that state today. GA4, Meta Pixel and TikTok were
 * to be loaded through Cloudflare Zaraz and no property id for any of them
 * exists, so the honest default is off.
 *
 * THE TWO SINKS
 *
 *   beacon    our own origin, `measure.record`. Anonymous by construction: the
 *             endpoint takes an event name and three words from closed
 *             vocabularies and has nowhere to put anything else. On only when
 *             `zentos_web.analytics.beacon` is true.
 *
 *   forward   a vendor tag that is already in the page. Zaraz injects itself
 *             at the Cloudflare edge and exposes `zaraz.track`; this file
 *             never loads it, never holds a property id, and hands over an
 *             event only when the site has named the transport AND the global
 *             is actually there. On only when `zentos_web.analytics.forward`
 *             names it.
 *
 * WHAT IS NEVER IN A PAYLOAD
 *
 * Anything a person typed. No name, phone, email, message, city or search
 * term; no path, query string or referrer; no id of any kind. An event is a
 * name plus at most three words chosen from lists that live in the Python
 * module. There is nothing here that could identify a visitor, which is what
 * makes it lawful to count somebody who has not consented to anything yet --
 * consent on this site is captured at submit, and every event before that
 * point has none behind it.
 *
 * WHERE THE CONFIG COMES FROM
 *
 * A `[data-zw-measure]` JSON block, rendered by `form_macros.measure_config()`
 * and emitted only when something is on. No block means no sinks, which is why
 * a page that never renders one -- and today that is every page -- measures
 * nothing and asks nobody.
 */

const CONFIG_SELECTOR = 'script[type="application/json"][data-zw-measure]';

/** The six client events. Mirrors measure.CLIENT_EVENTS; a name not in this
 *  list is dropped here rather than sent for the server to drop. */
export const EVENTS = {
	PAGE_VIEWED: "zw_page_viewed",
	FORM_STARTED: "zw_form_started",
	FORM_SUBMITTED: "zw_form_submitted",
	FORM_SUCCEEDED: "zw_form_succeeded",
	FORM_FAILED: "zw_form_failed",
	WHATSAPP_OPENED: "zw_whatsapp_opened",
};

const KNOWN_EVENTS = new Set(Object.keys(EVENTS).map((k) => EVENTS[k]));

/** Mirrors measure.PAGES. The last entry is where anything unrecognised goes. */
const PAGES = [
	"home",
	"products",
	"product",
	"contact",
	"projects",
	"distribution",
	"pro",
	"help",
	"blog",
	"about",
	"careers",
	"legal",
	"other",
];

/** Mirrors the subset of measure.REASONS the browser can produce. */
const REASONS = new Set(["name", "phone", "trade", "consent", "turnstile", "network", "server"]);

const KINDS = new Set(["preorder", "contact", "projects", "distribution", "pro"]);

const DIMENSIONS = { kind: KINDS, page: new Set(PAGES), reason: REASONS };

let config = null;

/**
 * Read the configuration block, once.
 *
 * Missing, empty or malformed all mean the same thing and mean it safely: no
 * beacon and no forwarding. A page whose analytics config fails to parse must
 * measure nothing, never guess.
 */
function readConfig() {
	if (config) return config;
	config = { endpoint: null, forward: [] };
	if (typeof document === "undefined") return config;
	const script = document.querySelector(CONFIG_SELECTOR);
	if (!script || !script.textContent.trim()) return config;
	try {
		const parsed = JSON.parse(script.textContent) || {};
		if (typeof parsed.endpoint === "string" && parsed.endpoint)
			config.endpoint = parsed.endpoint;
		if (Array.isArray(parsed.forward))
			config.forward = parsed.forward.filter((s) => typeof s === "string");
	} catch (err) {
		console.error("[zentos-web] measure config is not valid JSON", err);
	}
	return config;
}

/** Test seam: the harness swaps a config in without a server render. */
export function configure(next) {
	config = {
		endpoint: (next && next.endpoint) || null,
		forward: (next && Array.isArray(next.forward) ? next.forward : []).slice(),
	};
	return config;
}

/**
 * Which kind of page this is, from the path alone.
 *
 * A page kind rather than a path, because the path carries the query string
 * and therefore whatever a campaign put in it, and because `/products/aura`
 * and `/ar/products/aura` are one question asked twice. The language prefix is
 * stripped and never becomes a dimension: the two trees are the same funnel,
 * and a language split is a report, not a measurement.
 */
export function pageKind(pathname) {
	const path = String(pathname || "/").replace(/^\/ar(?=\/|$)/, "") || "/";
	const segments = path.split("/").filter(Boolean);
	if (!segments.length) return "home";
	const head = segments[0];
	// `/products` is the grid, `/products/<id>` is one product: different
	// questions, and the one that matters is whether a product page converts.
	if (head === "products") return segments.length > 1 ? "product" : "products";
	if (head === "business") return segments[1] === "partners" ? "distribution" : "projects";
	if (head === "help") return "help";
	if (head === "blog") return "blog";
	if (head === "terms" || head === "privacy" || head === "legal") return "legal";
	return PAGES.indexOf(head) === -1 ? "other" : head;
}

/** Frappe's CSRF token, emitted by base.html as a meta tag. */
function csrfToken() {
	const meta = document.querySelector('meta[name="csrf-token"]');
	return (meta && meta.getAttribute("content")) || "";
}

/** Drop anything not in vocabulary, here, so a bad call cannot reach anybody. */
function clean(payload) {
	const out = {};
	for (const name of Object.keys(DIMENSIONS)) {
		const value = payload && payload[name];
		if (typeof value === "string" && DIMENSIONS[name].has(value)) out[name] = value;
	}
	return out;
}

/**
 * Report to our own origin.
 *
 * `keepalive` rather than `sendBeacon`: a beacon cannot set headers, and this
 * request carries Frappe's CSRF token like every other POST on the site.
 * keepalive survives the page being navigated away from, which is the only
 * property sendBeacon was wanted for.
 *
 * Fire and forget in the strongest sense -- the response is not read and a
 * failure is not retried, logged or shown. Measurement must never be visible
 * to the person being measured, and it must never be able to break a form.
 */
function beacon(endpoint, event, dimensions) {
	try {
		fetch(endpoint, {
			method: "POST",
			credentials: "same-origin",
			keepalive: true,
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				"X-Frappe-CSRF-Token": csrfToken(),
			},
			body: JSON.stringify(Object.assign({ event: event }, dimensions)),
		}).catch(() => {});
	} catch (err) {
		/* a browser that cannot fetch is a browser that measures nothing */
	}
}

/**
 * Hand the event to a vendor transport that is already in the page.
 *
 * Both halves must hold: the site must have named the sink, and the global
 * must exist. Naming a sink that never arrives does nothing, and a tag that
 * arrives without being named is not talked to -- somebody else's script
 * appearing on the page is not our configuration.
 */
function forward(sinks, event, dimensions) {
	if (sinks.indexOf("zaraz") === -1) return;
	const zaraz = typeof window !== "undefined" ? window.zaraz : null;
	if (!zaraz || typeof zaraz.track !== "function") return;
	try {
		zaraz.track(event, dimensions);
	} catch (err) {
		/* a vendor tag that throws is the vendor's problem, not the form's */
	}
}

/**
 * Emit one event.
 *
 * @param {string} event  one of EVENTS
 * @param {object} [payload]  any of `kind`, `page`, `reason`
 * @returns {boolean} whether anything was sent anywhere. False is the normal,
 *   shipped answer today, and the harness asserts on it.
 */
export function track(event, payload) {
	if (!KNOWN_EVENTS.has(event)) return false;
	const cfg = readConfig();
	if (!cfg.endpoint && !cfg.forward.length) return false;

	const dimensions = clean(payload);
	if (cfg.endpoint) beacon(cfg.endpoint, event, dimensions);
	if (cfg.forward.length) forward(cfg.forward, event, dimensions);
	return true;
}

/**
 * The page was seen.
 *
 * Called once by the relevant entry point: the full island bundle on home,
 * product detail and form pages, and the measurement-only entry on the Products
 * chooser. The blog, Help Centre and legal texts ship neither entry and are not
 * counted. This is a measure of the funnel, not of the site's traffic; traffic
 * belongs to Cloudflare's own analytics, which needs nothing from this repo.
 */
export function trackPageView() {
	const path = typeof window !== "undefined" ? window.location.pathname : "/";
	return track(EVENTS.PAGE_VIEWED, { page: pageKind(path) });
}
